import type { ReactNode } from 'react';
import {
  Code,
  Heading,
  Icon,
  Link,
  Panel,
  PanelHeader,
  PanelSection,
  PanelTitle,
  Paragraph,
} from '@customerio/pluma-components/react';

const IDENTIFYING_PEOPLE_DOCS_URL = 'https://docs.customer.io/identifying-people';

type IdentifyPeoplePanelProps = {
  /** Every enabled identifier, `cio_id` included, in wire order. */
  allEnabledIdentifiers: readonly string[];
  /** The user-facing identifiers, which decide the singular or plural wording. */
  enabledIdentifierCount: number;
  isMultiIdentifier: boolean;
  workspaceSettingsHref: string;
  peoplePlural: string;
  peopleSingular: string;
};

/** Explains which identifiers the workspace uses, next to the snippet guide. */
export function IdentifyPeoplePanel({
  allEnabledIdentifiers,
  enabledIdentifierCount,
  isMultiIdentifier,
  workspaceSettingsHref,
  peoplePlural,
  peopleSingular,
}: IdentifyPeoplePanelProps) {
  return (
    // The grid stretches its children by default, which would run this panel down the height of the
    // much taller left column. Ember sized it to its content.
    <Panel alignSelf="start">
      <PanelHeader>
        <PanelTitle as="div" display="flex" alignItems="center">
          <Icon name="tip" size="lg" mr="100" />
          <Heading level="3" size="lg" id="how-to-identify-header">
            How do I identify {peoplePlural}?
          </Heading>
        </PanelTitle>
      </PanelHeader>
      <PanelSection>
        <Paragraph mb="200">
          An identifier is a unique value used to identify {peoplePlural} when adding, updating, or tracking a profile.
        </Paragraph>

        <Paragraph mb="200">
          This workspace has {joinIdentifierCodes(allEnabledIdentifiers)} enabled as{' '}
          {enabledIdentifierCount === 1 ? 'an identifier. That value' : 'identifiers. Those values'} must be unique for
          each {peopleSingular}.{' '}
          {isMultiIdentifier ? (
            <Link variant="secondary" href={workspaceSettingsHref}>
              View workspace settings
            </Link>
          ) : null}
          {isMultiIdentifier ? '.' : null}
        </Paragraph>

        <Paragraph>
          Learn more about{' '}
          <Link href={IDENTIFYING_PEOPLE_DOCS_URL} variant="secondary" isExternal>
            identifying {peoplePlural}
          </Link>
          .
        </Paragraph>
      </PanelSection>
    </Panel>
  );
}

/**
 * The identifier names as inline code, joined into an English list. Ember built
 * the same list as an HTML string; here they are elements, so nothing has to be
 * marked safe.
 */
function joinIdentifierCodes(identifiers: readonly string[]): ReactNode {
  return identifiers.map((identifier, index) => (
    <span key={identifier}>
      {separatorBefore(index, identifiers.length)}
      <Code>{identifier}</Code>
    </span>
  ));
}

/** Matches the Oxford-comma list `joinWords` produces: "a", "a and b", "a, b, and c". */
function separatorBefore(index: number, total: number): string {
  if (index === 0) {
    return '';
  }
  if (index === total - 1) {
    return total === 2 ? ' and ' : ', and ';
  }
  return ', ';
}
